import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";
import {
  AuthEntities,
  SetupPasswordValidation,
  DeviceAuthCheckApprovalEntities,
  DeviceAuthEntities,
  TokenVerificationAccountEntities,
  EnrollmentEntity,
  TwoFactorAuthEntity,
  Login2faEntity,
  OtpRequestResponseEntity
} from "@/domain/entities/Auth";
import {
  ResponsePayload,
  ResponsePayloadV2
} from "@/domain/entities/ResponsePayload";
import { AxiosResponse } from "axios";

export class AuthDataMapper {
  public convertAuthDataFromApi(result: AxiosResponse<any>): AuthEntities {
    const { data } = result;
    return new AuthEntities({
      token:  data.data.token,
      type: data.data.type,
      expiredAt: data.data.expired_at
    })
  }

  public convertDeviceAuthDataFromApi(result: AxiosResponse<any>) {
    const { data } = result;
    return new DeviceAuthEntities({
      needApproval: data.data.need_approval,
      approval: new AuthEntities({
        token: data.data.approval.token,
        type: data.data.approval.type,
        expiredAt: data.data.approval.expired_at
      }),
      login: new AuthEntities({
        token: data.data.login.token,
        type: data.data.login.type,
        expiredAt: data.data.login.expired_at 
      }),
      pairingCode: data.data.pairing_code
    })
  }

  public convertDeviceAuthCheckDataFromApi(result: AxiosResponse<any>) {
    const { data } = result;
    return new DeviceAuthCheckApprovalEntities({
      status: data.data.status,
      masterPhoneNumber: data.data?.master_phone_number,
      device: { token: data.data.device?.token },
      login: new AuthEntities({
        token: data.data.login?.token,
        type: data.data.login?.type,
        expiredAt: data.data.login?.expired_at
    })})
  }

  public convertForgotPasswordDataFromApi(
    result: AxiosResponse<any>
  ): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertValidateSetupPasswordLinkDataFromApi(
    result: AxiosResponse<any>
  ): SetupPasswordValidation {
    const { data } = result;
    return new SetupPasswordValidation({
      ...convertSnakeToCamel(data),
      ...convertSnakeToCamel(data.data)
    });
  }

  public convertResendSetupPasswordLinkDataFromApi(
    result: AxiosResponse<any>
  ): ResponsePayloadV2 {
    const { data } = result;
    return new ResponsePayloadV2(convertSnakeToCamel(data));
  }

  public convertResetPasswordDataFromApi(
    result: AxiosResponse<any>
  ): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertEditPasswordDataFromApi(
    result: AxiosResponse<any>
  ): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertLogoutDataFromApi(result: AxiosResponse<any>): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertVerificationAcountDataFromApi(
    result: AxiosResponse<any>
  ): TokenVerificationAccountEntities {
    const { data } = result;
    return new TokenVerificationAccountEntities({
      token: data.data.token,
      emailUser: data.data?.email_user,
      emailPartnerClient: data.data?.email_partner_client,
      phoneNumberPartnerClient: data.data?.phone_number_partner_client
    });
  }

  public convertResendOtpDataFromApi(
    result: AxiosResponse<any>
  ): TokenVerificationAccountEntities {
    const {
      data: { data }
    } = result;

    return new TokenVerificationAccountEntities(convertSnakeToCamel(data));
  }

  public convertVerificationOTPDataFromApi(
    result: AxiosResponse<any>
  ): boolean {
    const { data } = result;
    return data.data.is_valid;
  }

  public convertSetupPasswordDataFromApi(result: AxiosResponse<any>): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertLogin2faFromApi(result: AxiosResponse<any>): Login2faEntity {
    const { data } = result;
    const auth = data.data?.authentication;
    const tfa = data.data?.two_factor_authentication;
    return new Login2faEntity({
      authentication: auth?.token
        ? new AuthEntities({ token: auth.token, type: auth.type, expiredAt: auth.expired_at })
        : new AuthEntities(),
      twoFactorAuthentication: new TwoFactorAuthEntity({
        otpRequired: tfa?.otp_required ?? false,
        challengeToken: tfa?.challenge_token ?? "",
        expiresAt: tfa?.expires_at ?? "",
        enrollment: new EnrollmentEntity({
          enrolled: tfa?.enrollment?.enrolled ?? false,
          setupRequired: tfa?.enrollment?.setup_required ?? false
        })
      })
    });
  }

  public convertLogin2faOtpRequestFromApi(result: AxiosResponse<any>): OtpRequestResponseEntity {
    const { data } = result;
    return new OtpRequestResponseEntity({
      token: data.data?.token ?? "",
      otpExpiresInSeconds: data.data?.otp_expires_in_seconds ?? 0,
      expiresAt: data.data?.expires_at ?? "",
      maskedEmail: data.data?.masked_email ?? "",
      isResend: data.data?.is_resend ?? false,
      otpRegenerated: data.data?.otp_regenerated ?? false,
      resendAvailableAfterSeconds:
        data.data?.resend_available_after_seconds ?? 0,
      resendAvailableAt: data.data?.resend_available_at ?? ""
    });
  }

  public convertLogin2faOtpVerifyFromApi(result: AxiosResponse<any>): AuthEntities {
    const { data } = result;
    return new AuthEntities({
      token: data.data?.token ?? "",
      type: data.data?.type ?? "",
      expiredAt: data.data?.expired_at ?? ""
    });
  }
}
